import { useMemo } from "react";

/** DOM が肥大化してペインが固まらないよう、描画する行数・列数を制限する。 */
export const MAX_TABLE_ROWS = 1000;
export const MAX_TABLE_COLS = 100;

interface TableGridProps {
	/** 行ごとのセル値。行によって列数が違ってもよい (短い行は空セルで揃える)。 */
	rows: string[][];
}

/**
 * CSV / Excel プレビュー共通の表グリッド。ヘッダー行の有無はデータからは判定できない
 * ため推測せず、行番号付きの一様なグリッドとして描画する。
 */
export function TableGrid({ rows }: TableGridProps) {
	const visibleRows = rows.length > MAX_TABLE_ROWS ? rows.slice(0, MAX_TABLE_ROWS) : rows;
	const colCount = useMemo(
		() => Math.min(Math.max(0, ...visibleRows.map((row) => row.length)), MAX_TABLE_COLS),
		[visibleRows],
	);
	const rowsTruncated = rows.length > MAX_TABLE_ROWS;
	const colsTruncated = visibleRows.some((row) => row.length > MAX_TABLE_COLS);

	if (rows.length === 0) {
		return <p className="m-0 p-4 text-caption text-fg-3">データがありません</p>;
	}

	return (
		<div className="p-4">
			{(rowsTruncated || colsTruncated) && (
				<p className="m-0 mb-2 text-caption text-fg-3" data-testid="table-grid-truncated">
					大きなデータのため先頭のみ表示しています
					{rowsTruncated && ` (${MAX_TABLE_ROWS.toLocaleString()} 行まで)`}
					{colsTruncated && ` (${MAX_TABLE_COLS.toLocaleString()} 列まで)`}。
				</p>
			)}
			<table
				className="w-max max-w-none border-collapse select-text text-caption"
				data-testid="table-grid"
			>
				<tbody>
					{visibleRows.map((row, rowIndex) => (
						<tr key={rowIndex}>
							<td className="border border-line-1 bg-surface-2 px-2 py-0.5 text-right align-top font-mono text-fg-3">
								{rowIndex + 1}
							</td>
							{Array.from({ length: colCount }, (_, colIndex) => (
								<td
									key={colIndex}
									className="max-w-96 whitespace-pre-wrap border border-line-1 px-2 py-0.5 align-top text-fg-2"
								>
									{row[colIndex] ?? ""}
								</td>
							))}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}
